/**
 * 全ページ共通の SEO ヘッド。各ページで <PageHead title=... description=... path=... /> を置く。
 * React 19 ネイティブの metadata hoisting を利用（<title>/<meta>/<link> を直接描画すると
 * React が <head> に巻き上げ、ルート unmount 時に自動除去する）。
 * react-helmet-async は React 19 と非互換（タグを data-rh で管理できずルート遷移で累積する）ため不使用。
 * title は「{ページ固有}｜日本執事アカデミー」。canonical/OGP/Twitter/noindex 対応。
 */
interface PageHeadProps {
  title: string;
  description: string;
  path: string; // 例 "/courses/essential"（self-canonical）
  keywords?: string;
  ogImage?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
  rawTitle?: boolean; // true で SITE_NAME 連結を抑止
  lang?: 'ja' | 'en'; // 既定 'ja'
  /** ブログ記事ページで設定する citation_author メタ値 */
  citationAuthor?: string;
}

const SITE_URL = 'https://butler-academy.jp';
const SITE_NAME_JA = '日本執事アカデミー';
const SITE_NAME_EN = 'Japan Butler Academy';
// OG/Twitter 既定画像。専用 ogp.jpg (1200x630) を使用。
const DEFAULT_IMAGE = '/ogp.jpg';

export default function PageHead({
  title, description, path, keywords, ogImage, jsonLd, noindex, rawTitle, lang = 'ja', citationAuthor,
}: PageHeadProps) {
  const SITE_NAME = lang === 'en' ? SITE_NAME_EN : SITE_NAME_JA;
  const titleSep = lang === 'en' ? ' | ' : '｜';
  const fullTitle = rawTitle ? title : `${title}${titleSep}${SITE_NAME}`;
  // jaPath: canonical の ja 側パス（/en プレフィックスを除去して算出）
  const jaPath = path.replace(/^\/en(\/|$)/, '/').replace(/\/$/, '') || '/';
  const jaPathNorm = jaPath === '/' ? '/' : jaPath;
  const enPath = jaPathNorm === '/' ? '/en' : `/en${jaPathNorm}`;
  const canonicalUrl = lang === 'en' ? `${SITE_URL}${enPath}` : `${SITE_URL}${jaPathNorm}`;
  const ogLocale = lang === 'en' ? 'en_US' : 'ja_JP';
  const image = ogImage ? `${SITE_URL}${ogImage}` : `${SITE_URL}${DEFAULT_IMAGE}`;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      {citationAuthor && <meta name="citation_author" content={citationAuthor} />}

      {/* hreflang (ja / en / x-default) */}
      <link rel="alternate" hrefLang="ja" href={`${SITE_URL}${jaPathNorm}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${enPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${jaPathNorm}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      {lang === 'ja' && <meta property="og:locale:alternate" content="en_US" />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD 構造化データ（指定時のみ） */}
      {jsonLd && (
        <script
          type="application/ld+json"
          // React 19 は inline script を head に hoist しないため body 内に出力されるが、
          // JSON-LD はドキュメント内のどこにあってもクローラが解釈する。
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]),
          }}
        />
      )}
    </>
  );
}
